import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// Self-contained WhatsApp chat state — deliberately separate from inboxSlice
// (Instagram/generic inbox), per this feature's scope: the two messaging
// systems (and their backends) are not meant to share state or components.

export const fetchConversations = createAsyncThunk(
  'metaChat/fetchConversations',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/meta/conversations', { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch conversations');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'metaChat/fetchMessages',
  async (phone, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/meta/conversations/${encodeURIComponent(phone)}/messages`);
      return { ...data, phone };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch messages');
    }
  }
);

// Older history for the open thread — prepended above what's already loaded.
export const loadOlderMessages = createAsyncThunk(
  'metaChat/loadOlderMessages',
  async ({ phone, page }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/meta/conversations/${encodeURIComponent(phone)}/messages`, { params: { page } });
      return { ...data, phone };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load older messages');
    }
  }
);

export const sendTemplate = createAsyncThunk(
  'metaChat/sendTemplate',
  async ({ phone, templateName, language, components }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/meta/send', { recipient: phone, type: 'template', templateName, language, components });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to send template');
    }
  }
);

export const retryMessage = createAsyncThunk(
  'metaChat/retryMessage',
  async (messageId, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/meta/messages/${messageId}/retry`);
      return data.message;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to retry message');
    }
  }
);

export const sendReply = createAsyncThunk(
  'metaChat/sendReply',
  async ({ phone, text }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/meta/conversations/${encodeURIComponent(phone)}/reply`, { text });
      return data.message;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to send reply');
    }
  }
);

export const markThreadRead = createAsyncThunk(
  'metaChat/markThreadRead',
  async (phone, { rejectWithValue }) => {
    try {
      await api.post(`/meta/conversations/${encodeURIComponent(phone)}/read`);
      return phone;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to mark read');
    }
  }
);

export const markThreadAsLead = createAsyncThunk(
  'metaChat/markThreadAsLead',
  async ({ phone, contactName }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/meta/conversations/${encodeURIComponent(phone)}/mark-as-lead`, { contactName });
      return { phone, lead: data.lead };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to mark as lead');
    }
  }
);

const metaChatSlice = createSlice({
  name: 'metaChat',
  initialState: {
    conversations: [],
    conversationsMeta: {},
    activeRecipient: null,
    messages: [],
    messagesMeta: { page: 1, hasMore: false },
    // { open, expiresAt, reason } — whether free-form replies are still allowed.
    session: null,
    loading: false,
    loadingMore: false,
    loadingOlder: false,
    messagesLoading: false,
    sending: false,
    error: null,
  },
  reducers: {
    setActiveRecipient(state, action) {
      state.activeRecipient = action.payload;
      state.messages = [];
      state.messagesMeta = { page: 1, hasMore: false };
      state.session = null;
    },
    clearMetaChatError(state) {
      state.error = null;
    },
    // Socket-driven: a new inbound message arrived for this tenant.
    receiveMessage(state, action) {
      const msg = action.payload;
      if (state.activeRecipient === msg.recipient) {
        const exists = state.messages.find((m) => m._id === msg._id);
        if (!exists) state.messages.push(msg);
        // A customer message reopens the 24h free-form reply window.
        state.session = { open: true, expiresAt: new Date(Date.now() + 24 * 3_600_000).toISOString(), reason: null };
      }
      const conv = state.conversations.find((c) => c.recipient === msg.recipient);
      if (conv) {
        conv.lastMessage = msg;
        if (state.activeRecipient !== msg.recipient) conv.unreadCount = (conv.unreadCount || 0) + 1;
        state.conversations = [conv, ...state.conversations.filter((c) => c.recipient !== msg.recipient)];
      } else {
        state.conversations.unshift({ recipient: msg.recipient, contactName: msg.contactName || msg.recipient, unreadCount: 1, lastMessage: msg });
      }
    },
    // Socket-driven: a delivery/read tick for one of our own outbound sends.
    applyStatusUpdate(state, action) {
      const { messageId, status, error } = action.payload;
      const apply = (m) => {
        m.status = status;
        m.error = status === 'failed' ? error || m.error : undefined;
      };
      const msg = state.messages.find((m) => m._id === messageId);
      if (msg) apply(msg);
      // The list preview shows the last message's tick too, so keep it in step.
      state.conversations.forEach((c) => { if (c.lastMessage?._id === messageId) apply(c.lastMessage); });
    },
  },
  extraReducers: (builder) => {
    const pending = (state) => { state.loading = true; state.error = null; };
    const rejected = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(fetchConversations.pending, (state, action) => {
        if (action.meta.arg?.page > 1) state.loadingMore = true; else pending(state);
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        const { data, pagination } = action.payload;
        if (pagination?.page > 1) {
          const seen = new Set(state.conversations.map((c) => c.recipient));
          state.conversations.push(...data.filter((c) => !seen.has(c.recipient)));
        } else {
          state.conversations = data;
        }
        state.conversationsMeta = pagination;
      })
      .addCase(fetchConversations.rejected, (state, action) => { state.loadingMore = false; rejected(state, action); })

      .addCase(fetchMessages.pending, (state) => { state.messagesLoading = true; })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        // Ignore a response for a thread the agent has already navigated away from.
        if (action.payload.phone !== state.activeRecipient) return;
        state.messagesLoading = false;
        state.messages = action.payload.data;
        state.messagesMeta = { page: 1, hasMore: !!action.payload.pagination?.hasMore };
        state.session = action.payload.session || null;
      })
      .addCase(fetchMessages.rejected, (state) => { state.messagesLoading = false; })

      .addCase(loadOlderMessages.pending, (state) => { state.loadingOlder = true; })
      .addCase(loadOlderMessages.fulfilled, (state, action) => {
        state.loadingOlder = false;
        if (action.payload.phone !== state.activeRecipient) return;
        const seen = new Set(state.messages.map((m) => m._id));
        state.messages = [...action.payload.data.filter((m) => !seen.has(m._id)), ...state.messages];
        state.messagesMeta = { page: action.payload.pagination.page, hasMore: !!action.payload.pagination.hasMore };
      })
      .addCase(loadOlderMessages.rejected, (state, action) => { state.loadingOlder = false; state.error = action.payload; })

      .addCase(sendReply.pending, (state) => { state.sending = true; })
      .addCase(sendReply.fulfilled, (state, action) => {
        state.sending = false;
        const msg = action.payload;
        const exists = state.messages.find((m) => m._id === msg._id);
        if (!exists) state.messages.push(msg);
        const conv = state.conversations.find((c) => c.recipient === msg.recipient);
        if (conv) conv.lastMessage = msg;
      })
      .addCase(sendReply.rejected, (state, action) => { state.sending = false; state.error = action.payload; })

      .addCase(markThreadRead.fulfilled, (state, action) => {
        const conv = state.conversations.find((c) => c.recipient === action.payload);
        if (conv) conv.unreadCount = 0;
      })

      .addCase(markThreadAsLead.fulfilled, (state, action) => {
        const conv = state.conversations.find((c) => c.recipient === action.payload.phone);
        if (conv) conv.leadId = action.payload.lead._id;
      })

      .addMatcher(
        (a) => a.type === sendTemplate.fulfilled.type || a.type === retryMessage.fulfilled.type,
        (state, action) => {
          state.sending = false;
          const msg = action.payload;
          if (state.activeRecipient === msg.recipient && !state.messages.find((m) => m._id === msg._id)) state.messages.push(msg);
          const conv = state.conversations.find((c) => c.recipient === msg.recipient);
          if (conv) conv.lastMessage = msg;
        }
      )
      .addMatcher(
        (a) => a.type === sendTemplate.rejected.type || a.type === retryMessage.rejected.type,
        (state, action) => { state.sending = false; state.error = action.payload; }
      )
      .addMatcher((a) => a.type === sendTemplate.pending.type, (state) => { state.sending = true; });
  },
});

export const { setActiveRecipient, clearMetaChatError, receiveMessage, applyStatusUpdate } = metaChatSlice.actions;
export default metaChatSlice.reducer;
